import { countBy } from "lodash";
import { DateTime, DateTimeFormatOptions, Zone } from "luxon";

import {
  CaseReportSignature,
  ChargeCache,
  CliaCertificate,
  Clinic,
  Location,
  User,
} from "pwapi";

import { CaseDiagnosis, CptCode } from "custom-models";

import { SpecimenDetail } from "shared/reducers/specimenDetailReducer";

import {
  cptString,
  reduceCptCodes,
} from "components/viewer/dialogs/DiagnosisDialogHelpers";
import { getHighestVersionDiagnosis } from "components/viewer/dialogs/utils";

export const cptCodeStringFromDiagnoses = (diagnoses: CaseDiagnosis[]) =>
  reduceCptCodes(
    diagnoses
      .filter((diag) => diag?.cpt_codes && diag.cpt_codes.length > 0)
      .reduce((acc: CptCode[], diag) => [...acc, ...diag.cpt_codes!], [])
  )
    .filter((s) => s.code && s.code.code)
    .map((cpt) => `${cptString(cpt.code)}(${cpt.quantity || 1})`)
    .join(", ");

export const cptCodeStringFromChargeCaches = (charge_caches: ChargeCache[]) => {
  const cptStrings = charge_caches
    .map((c) => `${c.cpt_code?.code}${c.modifier ? `-${c.modifier}` : ""}`)
    .sort();
  return Object.entries(countBy(cptStrings))
    .map(([cptString, count]) => {
      return `${cptString}${count ? `(${count})` : ""}`;
    })
    .join(", ");
};

export const icd10StringFromDiagnoses = (diagnoses: CaseDiagnosis[]) =>
  diagnoses
    .reduce(
      (acc: string[], diag) => [
        ...acc,
        ...(diag?.icd10_code ? [diag.icd10_code] : []),
      ],
      []
    )
    .join(", ");

export const getLocationStringForSignature = (
  signature: CaseReportSignature
) => {
  if (signature.clia_certificate.suppress_location) return null;
  return getLocationString(
    signature.clia_certificate.location || signature.location
  );
};

export const getLocationString = (
  location: CliaCertificate["location"] | CaseReportSignature["location"]
) => {
  if (!location) return "";
  return `${location.street}${
    location.street2 ? `, ${location.street2}` : ""
  }, ${location.city ? `${location.city}, ` : ""}${
    location.state ? `${location.state} ` : ""
  }${location.zipcode ?? ""}`;
};

// TODO: We might want to use a library to do this
// I'm sure there are many, many edge cases
export const getPhoneString = (
  phone: string,
  style: "dot" | "parenthesis" = "parenthesis"
): string | undefined => {
  const cleanPhone = phone && phone.replace(/\D*/g, "");
  return cleanPhone && cleanPhone.length > 8
    ? style === "parenthesis"
      ? `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(
          3,
          6
        )}-${cleanPhone.slice(6)}`
      : `${cleanPhone.slice(0, 3)}.${cleanPhone.slice(3, 6)}.${cleanPhone.slice(
          6
        )}`
    : undefined;
};

export const getDiagnosesObject = (
  specimenDetail: SpecimenDetail[],
  reportState:
    | "signout"
    | "addon"
    | "preliminary"
    | "final"
    | "preview"
    | "pending",
  currentUser?: User
) => {
  return specimenDetail.map((specimen) => {
    let diagnosis = undefined;
    let screenDiagnosis = getHighestVersionDiagnosis(
      specimen.diagnoses,
      CaseDiagnosis.source.SCREEN
    );

    if (reportState === "signout") {
      diagnosis = specimen.diagnoses.find(
        (diag) =>
          diag.user && diag.user.id === currentUser?.id && !diag.is_completed
      );
      if (!diagnosis) {
        diagnosis = screenDiagnosis
          ? { ...screenDiagnosis, is_screen_valid: true }
          : undefined;
      }
    } else if (reportState === "addon") {
      diagnosis = specimen.diagnoses.find(
        (diag) =>
          diag.user &&
          diag.user.id === currentUser?.id &&
          !diag.is_completed &&
          (diag.source === CaseDiagnosis.source.ADDEND ||
            diag.source === CaseDiagnosis.source.AMEND ||
            diag.source === CaseDiagnosis.source.CORRECTION)
      );
    } else {
      diagnosis = specimen.diagnoses
        .filter((diag) => diag.is_completed && diag.version)
        .reduce((prev: CaseDiagnosis | undefined, curr) => {
          if (curr.source === CaseDiagnosis.source.LIS_FINALIZED) {
            return prev;
          }
          return prev && (prev.version || -1) > (curr.version || -1)
            ? prev
            : curr;
        }, undefined);
    }

    return {
      specimen: specimen,
      diagnosis: diagnosis,
      screenDiagnosis: screenDiagnosis,
    };
  });
};

export const dateWithTimezone = (
  date: string,
  fallbackTimezone?: Location.timezone | Zone
) => {
  // NOTE: currently all times back from the server as UTC. We are telling
  // DateTime explicitly to parse these and _keep_ them as UTC, versus trying
  // to convert them to the local timezone
  // This will automatically keep them as the timezone as described in the string
  // ... once the backend starts "doing that"
  let dt = DateTime.fromISO(date, { setZone: true });
  // If the zone is in fact in UTC, we want to force it to a timezone
  // ... so it doesn't "drag" the time into the next day in display
  if (dt.offsetNameShort === "UTC" && fallbackTimezone) {
    // The best guess we have towards a "report timezone" is the signout timezone
    // ... of the pathologist. It _should_ be the "clinic timezone" and that's
    // ... given by ModMed in their collection date offset. We may want to consider
    // ... other options of setting or assuming a "clinic timezone", but this is
    // ... the best we have for now.
    dt = dt.setZone(fallbackTimezone);
  }

  if (dt.isValid) {
    // NOTE: in the future, we should consider .toLocaleString(DateTime.DATE_SHORT)
    // ... for multiple / overseas markets (plus, general "correctness"/standardization)
    return dt.toFormat("MM/dd/yyyy");
  } else {
    // If the timezone was a bad string for any reason, we bail here.
    return dt.invalidReason;
  }
};

export const PW_DATETIME_WITH_ZONE: DateTimeFormatOptions = {
  month: "numeric",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
};
export const DATETIME_SHORT_WITH_SECONDS_AND_TZ: typeof DateTime.DATETIME_SHORT_WITH_SECONDS =
  {
    ...DateTime.DATETIME_SHORT_WITH_SECONDS,
    timeZoneName: "long",
  };

export const scannerTypeToManufacturer = (
  scanner_type?: string,
  name?: string | null
) => {
  if (scanner_type?.toLowerCase().includes("pannoramic")) return "3DHISTECH";
  if (scanner_type?.toLowerCase().includes("gt450")) return "Leica";
  if (!scanner_type && name?.endsWith(".svs")) return "Leica";
  if (!scanner_type) return undefined;
  return scanner_type;
};

export const sameAddress = (
  location1?: Location | null,
  location2?: Location | null
) => {
  if (!location1 || !location2) return false;
  return getLocationString(location1) === getLocationString(location2);
};

export const seeNoteOnDiagnosis = (
  diagnosis?: string,
  note?: string | null
) => {
  if (
    note?.trim() &&
    diagnosis?.trim() &&
    !diagnosis.trimEnd().toUpperCase().includes("SEE NOTE")
  )
    return `${diagnosis.trimEnd()} (SEE NOTE)`;
  return diagnosis;
};

const CRITICAL_RESULT_ICD10 = ["C43", "C44.90", "C4A", "C83.3", "D03", "L95.9"];

const CRITICAL_RESULT_ZSD = [
  "merkel cell carcinoma",
  "residual malignant melanoma in situ and scar secondary to a previous procedure",
  "residual malignant melanoma and scar with chronic inflammation secondary to a previous procedure",
  "metastatic melanoma",
  "melanoma in situ",
  "melanoma in situ, lentigo maligna type",
  "malignant lymphoma, diffuse large b-cell",
  "atypical fibroxanthoma",
  "angiosarcoma",
  "dermatofibrosarcoma protuberans",
  "extramammary paget's disease",
  "metastatic adenocarcinoma",
  "malignant fibrous histiocytoma",
  "leiomyosarcoma",
  "malignant neoplasm, nos",
  "microcystic adnexal carcinoma",
  "paget's disease",
  "sebaceous carcinoma",
  "leukocytoclastic vasculitis",
  "vasculitis, nos",
];

const CRITICAL_RESULT_CLINIC_NAMES = [
  "southside dermatology - sunbeam",
  "southside dermatology - jax beach",
];

const CRITICAL_RESULT_CLINIC_D_NUMBERS = [
  "D1332", // Southside Dermatology - Sunbeam
  "D1333", // Southside Dermatology - Jax Beach
];

export const isDiagnosisCritical = (diagnosis?: CaseDiagnosis) => {
  if (!diagnosis) return false;

  const icd10Critical = diagnosis.icd10_code
    ? CRITICAL_RESULT_ICD10.some((code) =>
        diagnosis.icd10_code?.toUpperCase().startsWith(code)
      )
    : false;

  const zsdCritical = diagnosis.zsd_code
    ? CRITICAL_RESULT_ZSD.some((code) =>
        diagnosis.zsd_code?.toLowerCase().includes(code.toLowerCase())
      )
    : false;

  return icd10Critical || zsdCritical;
};

export const isDiagnosisRed = (clinic?: Clinic) => {
  if (!clinic) return false;

  if (
    !CRITICAL_RESULT_CLINIC_NAMES.includes(clinic.name?.toLowerCase()) &&
    !CRITICAL_RESULT_CLINIC_D_NUMBERS.includes(
      clinic.external_clinic_id?.toUpperCase() || ""
    )
  )
    return false;

  return true;
};
